import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TokenDto, ApiError } from "../../types/api";
import { STORAGE_KEYS } from "../../constants";
import { extractErrorMessage } from "../utils";

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  phoneNumber: string;
  dateOfBirth: string;
  roleId: number;
  roleTitle: string;
  status: number;
  deaNumber?: string;
  dentalLicenseNumber?: string;
  npiNumber?: string;
  specialtyLicenseNumber?: string;
  specialtyIds?: number[];
  departments?: string[];
  avatar?: string;
  isEmailVerified?: boolean;
  lastLoginAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthState {
  user: User | null;
  token: TokenDto | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  registrationToken: string | null;
}

// Helper function to load token from localStorage
const loadTokenFromStorage = (): TokenDto | null => {
  try {
    const tokenData = localStorage.getItem(STORAGE_KEYS.TOKEN);
    return tokenData ? JSON.parse(tokenData) : null;
  } catch (error) {
    return null;
  }
};

// Helper function to load registration token from localStorage
const loadRegistrationTokenFromStorage = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.REGISTRATION_TOKEN);
};

const initialState: AuthState = {
  user: null,
  token: loadTokenFromStorage(),
  isAuthenticated: !!loadTokenFromStorage(),
  isLoading: false,
  error: null,
  registrationToken: loadRegistrationTokenFromStorage(),
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Login actions
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
      // Clear registration token when starting login
      state.registrationToken = null;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{ user: User; token: TokenDto }>,
    ) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.error = null;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.error = action.payload;
    },
    loginComplete: (state) => {
      state.isLoading = false;
      state.error = null;
    },

    // Two-factor authentication actions
    twoFactorStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    twoFactorSuccess: (
      state,
      action: PayloadAction<{ user: User; token: TokenDto }>,
    ) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.error = null;
    },
    twoFactorFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.error = action.payload;
    },

    // Registration actions
    registrationStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    registrationSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
    },
    registrationFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    setRegistrationToken: (state, action: PayloadAction<string>) => {
      state.registrationToken = action.payload;
    },

    // Logout action
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.error = null;
      state.registrationToken = null;
    },
    tokenRefreshed: (state, action: PayloadAction<{ token: TokenDto }>) => {
      state.token = action.payload.token;
      state.error = null;
    },

    // Utility actions
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    initializeAuth: (_state) => {
      // This action triggers middleware to check if user data needs to be loaded
      // No state changes needed here, just triggers the middleware
    },
  },
  extraReducers: (builder) => {
    // Handle all rejected auth thunks automatically
    builder.addMatcher(
      (action) =>
        action.type.startsWith("auth/") && action.type.endsWith("/rejected"),
      (state, action) => {
        state.isLoading = false;
        // Extract error message from ApiError payload
        const error = action.payload as ApiError | undefined;
        state.error = error
          ? extractErrorMessage(error)
          : "An error occurred. Please try again.";
      },
    );
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  loginComplete,
  twoFactorStart,
  twoFactorSuccess,
  twoFactorFailure,
  registrationStart,
  registrationSuccess,
  registrationFailure,
  setRegistrationToken,
  logout,
  tokenRefreshed,
  clearError,
  updateUser,
  setUser,
  initializeAuth,
} = authSlice.actions;

export default authSlice.reducer;
